import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { agencyLogin } from "../lib/api";
import { agencyAuth } from "../lib/identity";
import { useAgencyAuth } from "../lib/useIdentity";
import { Building2, ArrowLeft } from "lucide-react";
import { MaliStripe } from "../components/MaliStripe";
import { useT } from "../lib/lang";

export default function AgencyLoginPage() {
  const [t] = useT();
  const [session] = useAgencyAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (session) return <Navigate to="/admin/agency" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      const r = await agencyLogin(email.trim(), pwd);
      // Cookies (rr_agency_token + rr_agency_csrf) were set by the
      // backend. We only persist the agency profile for UI rendering.
      agencyAuth.set(r.agency);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Connexion impossible");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="agency-login-page">
      <MaliStripe />
      <div className="bg-rescue-ink text-white px-5 py-6">
        <Link
          to="/admin/login"
          data-testid="agency-back-admin"
          className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase opacity-80 hover:opacity-100"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Admin
        </Link>
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-90 flex items-center gap-2 mt-3">
          <Building2 className="w-3.5 h-3.5 text-mali-yellow" /> {t.agencyAccess}
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-2">{t.agencyLoginTitle}</h1>
        <p className="text-sm opacity-90 mt-3">{t.agencyLoginSubtitle}</p>
      </div>

      <form onSubmit={submit} className="mx-5 mt-5 mb-4 border-2 border-rescue-ink bg-white p-4 space-y-3">
        <div>
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute" htmlFor="agency-email">
            {t.agencyEmail}
          </label>
          <input
            id="agency-email"
            type="email"
            autoFocus
            required
            data-testid="agency-email-input"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
            placeholder="dispatch@samu.ml"
            className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-3 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute" htmlFor="agency-pwd">
            {t.agencyPassword}
          </label>
          <input
            id="agency-pwd"
            type="password"
            required
            data-testid="agency-password-input"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); if (err) setErr(""); }}
            placeholder="••••••••"
            className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-3 text-sm font-mono"
          />
        </div>
        {err && (
          <div className="text-xs text-rescue-red font-bold uppercase tracking-[0.15em]" data-testid="agency-login-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="agency-login-btn"
          className="w-full bg-rescue-red text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-ink transition-colors disabled:opacity-60"
        >
          {submitting ? "Connexion…" : t.agencyLoginCta}
        </button>
      </form>
    </div>
  );
}
